import * as TabsPrimitive from '@radix-ui/react-tabs';
import { createContext, forwardRef, useContext, type ComponentPropsWithoutRef } from 'react';
import { cn } from '../../lib/utils';
import './Tabs.css';

/** underline: the default, an accent underline under the selected label.
 *  cell: a document page's tab bar (PageLayoutDocument's bar), the selected
 *  tab a paper cell with a brand rule on top, joined to the page beneath.
 *  The look lives in Tabs.css as plain classes. */
export type TabsVariant = 'underline' | 'cell';

const TabsVariantContext = createContext<TabsVariant>('underline');

/* ----------------------------------------
 * Tabs Root
 * ---------------------------------------- */
export interface TabsProps extends ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: TabsVariant;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(({ variant = 'underline', ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.Root ref={ref} {...props} />
  </TabsVariantContext.Provider>
));

Tabs.displayName = 'Tabs';

/* ----------------------------------------
 * Tabs List
 * ---------------------------------------- */
export interface TabsListProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.List> {}

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  ({ className, ...props }, ref) => {
    const variant = useContext(TabsVariantContext);
    return (
      <TabsPrimitive.List
        ref={ref}
        className={cn(variant === 'cell' ? 'tabs-cell' : 'flex gap-ds-1 border-b border-ds-border', className)}
        {...props}
      />
    );
  }
);

TabsList.displayName = 'TabsList';

/* ----------------------------------------
 * Tabs Trigger
 * ---------------------------------------- */
export interface TabsTriggerProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ className, ...props }, ref) => {
    const variant = useContext(TabsVariantContext);
    return (
      <TabsPrimitive.Trigger
        ref={ref}
        className={cn(
          variant === 'cell'
            ? 'tabs-cell__tab type-item'
            : 'py-ds-2 px-ds-4 font-ds-sans text-sm font-medium text-ds-text-secondary bg-transparent border-none border-b-2 border-b-transparent -mb-px cursor-pointer data-[state=active]:text-ds-text data-[state=active]:border-b-ds-accent',
          className
        )}
        {...props}
      />
    );
  }
);

TabsTrigger.displayName = 'TabsTrigger';

/* ----------------------------------------
 * Tabs Content
 * ---------------------------------------- */
export interface TabsContentProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {}

export const TabsContent = forwardRef<HTMLDivElement, TabsContentProps>(
  ({ className, ...props }, ref) => {
    const variant = useContext(TabsVariantContext);
    return (
      <TabsPrimitive.Content
        ref={ref}
        // A cell tab's pane starts where its page sets it.
        className={cn(variant === 'cell' ? 'tabs-cell__pane' : 'pt-ds-4', className)}
        {...props}
      />
    );
  }
);

TabsContent.displayName = 'TabsContent';
